'use client';

import {
  Dispatch,
  FunctionComponent,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from 'react';

interface RunningPipeline {
  jobId: string;
  stageSlug: string | null;
}

interface AppState {
  chatOpen: boolean;
  runningPipelines: Record<string, RunningPipeline>;
  // Structured text describing what the user is currently looking at on screen.
  // Any view can write here; GlobalChatPanel reads it and injects it into every
  // message sent to Matilda so she knows the exact UI context.
  screenContext: string | null;
  // Text any view can push into the global chat textarea (e.g. a suggestion
  // card or a timeline task). GlobalChatPanel applies it, then clears it.
  chatPrefill: string | null;
  // Lets a view have Matilda *speak first*: she posts a framing line plus
  // clickable options, instead of waiting for the user to type. Used by the
  // "Tell Matilda what to do" hand-off. GlobalChatPanel renders it, then clears.
  chatSeed: { context: string; options: string[] } | null;
  // Unread inbox-thread count for the current user, rendered as the nav badge.
  // Held here so the inbox view can adjust it directly rather than via a
  // window event.
  inboxUnread: number;
  // Seeded from the server in the root layout so the navbar can drop
  // owner-only destinations on the first paint instead of fetching a role and
  // flashing a link the user cannot open.
  isOwner: boolean;
}

interface AppStateValue extends AppState {
  setChatOpen: (open: boolean) => void;
  markPipelineStarted: (employerId: string, jobId: string, stageSlug: string | null) => void;
  markPipelineEnded: (employerId: string) => void;
  setScreenContext: (ctx: string | null) => void;
  setChatPrefill: (text: string | null) => void;
  setChatSeed: (seed: { context: string; options: string[] } | null) => void;
  setInboxUnread: Dispatch<SetStateAction<number>>;
}

const AppStateContext = createContext<AppStateValue | null>(null);

export const AppStateProvider: FunctionComponent<{ children: ReactNode; isOwner: boolean }> = ({
  children,
  isOwner,
}) => {
  const [chatOpen, setChatOpen] = useState(false);
  const [runningPipelines, setRunningPipelines] = useState<Record<string, RunningPipeline>>({});
  const [screenContext, setScreenContext] = useState<string | null>(null);
  const [chatPrefill, setChatPrefill] = useState<string | null>(null);
  const [chatSeed, setChatSeed] = useState<{ context: string; options: string[] } | null>(null);
  const [inboxUnread, setInboxUnread] = useState(0);

  const markPipelineStarted = (employerId: string, jobId: string, stageSlug: string | null): void => {
    setRunningPipelines(prev => ({ ...prev, [employerId]: { jobId, stageSlug } }));
  };

  const markPipelineEnded = (employerId: string): void => {
    setRunningPipelines(prev => {
      if (!(employerId in prev)) return prev;
      const next = { ...prev };
      delete next[employerId];
      return next;
    });
  };

  return (
    <AppStateContext.Provider
      value={{
        chatOpen,
        runningPipelines,
        screenContext,
        chatPrefill,
        chatSeed,
        setChatOpen,
        markPipelineStarted,
        markPipelineEnded,
        setScreenContext,
        setChatPrefill,
        setChatSeed,
        inboxUnread,
        setInboxUnread,
        isOwner,
      }}
    >
      {children}
    </AppStateContext.Provider>
  );
};

export function useAppState(): AppStateValue {
  const ctx = useContext(AppStateContext);
  if (!ctx) throw new Error('useAppState must be used within AppStateProvider');
  return ctx;
}
